import {useState, useEffect} from 'react';
import {useParams, useNavigate, Link} from 'react-router';

import * as AppointmentApi from '../api/AppointmentApi';
import PageShell from '../components/PageShell';
import {localDate, localTime} from '../components/rescheduleSlots';
import './AppointmentAdd.css'; // Use the same css for styling consistency

export default function AppointmentDetail() {
  const {appointmentId} = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AppointmentApi.getAppointmentById(appointmentId)
      .then((appointmentData) => {
        setAppointment(appointmentData);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load appointment details', err);
        setLoading(false);
      });
  }, [appointmentId]);

  if (loading) {
    return (
      <PageShell title="Appointment Details" showBackButton={true}>
        <div style={{textAlign: 'center', marginTop: '2rem'}}>Loading...</div>
      </PageShell>
    );
  }

  if (!appointment) {
    return (
      <PageShell title="Appointment Details" showBackButton={true}>
        <div style={{textAlign: 'center', marginTop: '2rem'}}>Appointment not found.</div>
      </PageShell>
    );
  }

  const tz =
    appointment.patient_timezone || appointment.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const start = appointment.start_time || appointment.appointment_date;
  // Show the time in the appointment's own timezone (what the patient chose), not the browser's.
  const dateStr = localDate(start, tz);
  const timeStr = localTime(start, tz);
  const alertBeforeSet = new Set(AppointmentApi.alertBeforeLabels(start, appointment.reminder_alerts || []));

  return (
    <PageShell title="Appointment Details" showBackButton={true}>
      <div className="appt-add-container">
        <div className="appt-form-card" style={{pointerEvents: 'none'}}>
          <div className="appt-field">
            <span className="appt-field-label" id="appt-detail-date">
              Date
            </span>
            <div
              className="appt-input"
              style={{background: '#F9FAFB', display: 'flex', alignItems: 'center'}}
              data-testid="appointment-detail-date"
            >
              {dateStr}
            </div>
          </div>

          <div className="appt-field">
            <span className="appt-field-label" id="appt-detail-time">
              Time
            </span>
            <div
              className="appt-input"
              style={{background: '#F9FAFB', display: 'flex', alignItems: 'center'}}
              data-testid="appointment-detail-time"
            >
              {timeStr}
            </div>
          </div>

          <div className="appt-field">
            <span className="appt-field-label" id="appt-detail-tz">
              Timezone
            </span>
            <div
              className="appt-input"
              style={{background: '#F9FAFB', display: 'flex', alignItems: 'center'}}
            >
              {tz}
            </div>
          </div>

          <div className="appt-field">
            <label className="appt-field-label" htmlFor="appt-detail-note">
              Note for yourself
            </label>
            <textarea
              id="appt-detail-note"
              className="appt-textarea"
              value={appointment.note || appointment.notes || 'No notes provided.'}
              readOnly
              style={{background: '#F9FAFB'}}
            />
          </div>

          <fieldset className="choice-group-fieldset">
            <legend className="choice-group-legend">Alert methods</legend>

            <div className={`choice-row ${appointment.email_alert ? 'checked' : ''}`}>
              <div className="custom-checkbox" />
              <div className="choice-row-content">
                <h4 className="choice-row-label">Email</h4>
              </div>
            </div>

            <div className={`choice-row ${appointment.sms_alert ? 'checked' : ''}`}>
              <div className="custom-checkbox" />
              <div className="choice-row-content">
                <h4 className="choice-row-label">SMS</h4>
              </div>
            </div>
          </fieldset>

          <fieldset className="choice-group-fieldset">
            <legend className="choice-group-legend">Alert before appointment</legend>

            {['1 hour', '1 day', '1 week'].map((time) => {
              const isChecked = alertBeforeSet.has(time);
              return (
                <div key={time} className={`choice-row ${isChecked ? 'checked' : ''}`}>
                  <div className="custom-checkbox" />
                  <div className="choice-row-content">
                    <h4 className="choice-row-label">{time}</h4>
                  </div>
                </div>
              );
            })}
          </fieldset>
        </div>

        <div style={{marginTop: '16px', display: 'flex', gap: '12px'}}>
          <Link
            to={`/appointments/${appointmentId}/edit`}
            className="primary-btn"
            style={{
              flex: 1,
              textAlign: 'center',
              textDecoration: 'none',
              background: '#4F46E5',
              pointerEvents: 'auto',
            }}
          >
            Edit reminder
          </Link>
          <button
            onClick={() => navigate('/appointments/')}
            className="primary-btn"
            style={{flex: 1, background: '#6B7280', color: 'white', pointerEvents: 'auto'}}
          >
            Back to list
          </button>
        </div>
      </div>
    </PageShell>
  );
}
